import React, { useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { FiSearch, FiChevronRight, FiChevronDown, FiHash } from 'react-icons/fi'

// ─── Search logic ────────────────────────────────────────────────────
function filterData(categories, query) {
  if (!query.trim()) return categories

  const q = query.toLowerCase().trim()

  return categories
    .map(category => {
      const categoryNameMatch = category.name.toLowerCase().includes(q)

      const filteredSubs = category.items
        .map(sub => {
          const subNameMatch = sub.name.toLowerCase().includes(q)

          const filteredItems = sub.items.filter(item => {
            const codeStr = String(item.bop_code)
            const subCodeStr = String(item.sub_code)
            return (
              categoryNameMatch ||
              subNameMatch ||
              codeStr.includes(q) ||
              subCodeStr.includes(q)
            )
          })

          if (filteredItems.length === 0) return null
          return { ...sub, items: filteredItems }
        })
        .filter(Boolean)

      if (filteredSubs.length === 0) return null
      return { ...category, items: filteredSubs }
    })
    .filter(Boolean)
}

// ─── Component ───────────────────────────────────────────────────────
function BopCodeComponent({ data, onSelect, selectedId }) {
  const [search, setSearch] = useState('')
  const [expandedCats, setExpandedCats] = useState({})
  const [expandedSubs, setExpandedSubs] = useState({})

  const filtered = useMemo(() => filterData(data, search), [data, search])
  const isSearching = search.trim().length > 0

  const toggleCat = useCallback((name) => {
    setExpandedCats(prev => ({ ...prev, [name]: !prev[name] }))
  }, [])

  const toggleSub = useCallback((catName, subName) => {
    const key = `${catName}::${subName}`
    setExpandedSubs(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const isCatExpanded = (name) => isSearching || !!expandedCats[name]
  const isSubExpanded = (catName, subName) =>
    isSearching || !!expandedSubs[`${catName}::${subName}`]

  const handleSelect = (item) => {
    if (onSelect) {
      onSelect(item)
    }
  }

  const isSelected = (item) =>
    selectedId != null && selectedId === item.id

  return (
    <Wrapper>
      {/* Search bar */}
      <SearchContainer>
        <SearchIcon><FiSearch size={16} /></SearchIcon>
        <SearchInput
          type="text"
          placeholder="Search by BOP code, sub code, or category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <ClearBtn onClick={() => setSearch('')}>&times;</ClearBtn>
        )}
      </SearchContainer>

      {/* Results */}
      <ResultsContainer>
        {filtered.length === 0 && (
          <EmptyState>
            <FiHash size={24} />
            <p>No BOP codes match your search.</p>
          </EmptyState>
        )}

        {filtered.map(category => (
          <CategoryBlock key={category.name}>
            <CategoryHeader onClick={() => toggleCat(category.name)}>
              <ChevronWrap>
                {isCatExpanded(category.name)
                  ? <FiChevronDown size={16} />
                  : <FiChevronRight size={16} />}
              </ChevronWrap>
              <CategoryName>{category.name}</CategoryName>
              <CountBadge>
                {category.items.reduce((sum, s) => sum + s.items.length, 0)}
              </CountBadge>
            </CategoryHeader>

            {isCatExpanded(category.name) && (
              <SubList>
                {category.items.map(sub => (
                  <SubBlock key={sub.name}>
                    <SubHeader onClick={() => toggleSub(category.name, sub.name)}>
                      <ChevronWrap>
                        {isSubExpanded(category.name, sub.name)
                          ? <FiChevronDown size={14} />
                          : <FiChevronRight size={14} />}
                      </ChevronWrap>
                      <SubName>{sub.name}</SubName>
                      <CountBadge $small>{sub.items.length}</CountBadge>
                    </SubHeader>

                    {isSubExpanded(category.name, sub.name) && (
                      <ItemsList>
                        {sub.items.map(item => (
                          <ItemRow
                            key={item.id}
                            onClick={() => handleSelect(item)}
                            $selected={isSelected(item)}
                          >
                            <CodeGroup>
                              <BopCode>{item.bop_code}&nbsp;</BopCode>
                              {item.sub_code != null && String(item.sub_code).trim() !== '' && String(item.sub_code).trim() !== '0' && (
                                <SubCode> | {item.sub_code}</SubCode>
                              )}
                            </CodeGroup>
                            <ItemDescription>{item.description}</ItemDescription>
                          </ItemRow>
                        ))}
                      </ItemsList>
                    )}
                  </SubBlock>
                ))}
              </SubList>
            )}
          </CategoryBlock>
        ))}
      </ResultsContainer>
    </Wrapper>
  )
}

export default BopCodeComponent

BopCodeComponent.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    items: PropTypes.array,
  })).isRequired,
  onSelect: PropTypes.func,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

// ─── Styled Components ───────────────────────────────────────────────

const Wrapper = styled.div`
  width: 100%;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
  overflow: hidden;
`

const SearchContainer = styled.div`
  position: relative;
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
`

const SearchIcon = styled.span`
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 36px 10px 40px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #212529;
  background: #fff;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder {
    color: #adb5bd;
  }

  &:focus {
    border-color: #0d6efd;
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12);
  }
`

const ClearBtn = styled.button`
  position: absolute;
  right: 28px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;

  &:hover {
    color: #212529;
  }
`

const ResultsContainer = styled.div`
  max-height: 480px;
  overflow-y: auto;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  color: #adb5bd;
  font-size: 0.875rem;
`

const CategoryBlock = styled.div`
  border-bottom: 1px solid #f1f3f5;

  &:last-child {
    border-bottom: none;
  }
`

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  transition: background 0.1s;

  &:hover {
    background: #f8f9fa;
  }
`

const ChevronWrap = styled.span`
  color: #6c757d;
  display: inline-flex;
  flex-shrink: 0;
`

const CategoryName = styled.span`
  font-weight: 700;
  font-size: 0.9rem;
  color: #212529;
`

const CountBadge = styled.span`
  margin-left: auto;
  font-size: ${p => p.$small ? '0.7rem' : '0.75rem'};
  font-weight: 600;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 8px;
  border-radius: 999px;
`

const SubList = styled.div`
  padding-left: 16px;
`

const SubBlock = styled.div`
  border-top: 1px solid #f1f3f5;
`

const SubHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  user-select: none;
  transition: background 0.1s;

  &:hover {
    background: #f8f9fa;
  }
`

const SubName = styled.span`
  font-weight: 600;
  font-size: 0.85rem;
  color: #495057;
`

const ItemsList = styled.div`
  padding: 0 8px 8px 24px;
`

const ItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.1s;
  background: ${p => p.$selected ? 'rgba(13, 110, 253, 0.08)' : 'transparent'};
  border: 1px solid ${p => p.$selected ? '#0d6efd' : 'transparent'};

  &:hover {
    background: ${p => p.$selected ? 'rgba(13, 110, 253, 0.12)' : '#f8f9fa'};
  }
`

const CodeGroup = styled.div`
  display: flex;
  align-items: baseline;
  flex-shrink: 0;
  min-width: 64px;
`

const BopCode = styled.span`
  font-weight: 700;
  font-size: 0.875rem;
  color: #0d6efd;
  font-variant-numeric: tabular-nums;
`

const SubCode = styled.span`
  font-weight: 600;
  font-size: 0.8rem;
  color: #6c757d;
  font-variant-numeric: tabular-nums;
`

const ItemDescription = styled.span`
  font-size: 0.825rem;
  color: #495057;
  line-height: 1.4;
`